
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const About = () => {
  const { t } = useTranslation();

  const stats = [
    { number: '15+', label: t('yearsExperience') },
    { number: '5000+', label: t('successfulPlacements') },
    { number: '200+', label: t('partnerCompanies') },
    { number: '5', label: t('countries') },
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Integrity',
      description: 'We maintain the highest standards of honesty and transparency in all our dealings with clients and candidates.',
    },
    {
      icon: 'ri-award-line',
      title: 'Excellence',
      description: 'We strive for excellence in every placement, ensuring quality matches that benefit both employers and employees.',
    },
    {
      icon: 'ri-team-line',
      title: 'Partnership',
      description: 'We build long-term partnerships with our clients, understanding their unique needs and providing tailored solutions.',
    },
    {
      icon: 'ri-global-line',
      title: 'Global Reach',
      description: 'Our extensive network across the GCC region enables us to connect talent with opportunities seamlessly.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('aboutTitle')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('aboutSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                {t('aboutDescription')}
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Since our establishment, we have successfully placed thousands of skilled professionals in various sectors across the GCC region. Our deep understanding of the local job markets, combined with our commitment to quality service, has made us a trusted partner for both job seekers and employers.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We take pride in our comprehensive approach to recruitment, which includes thorough candidate screening, skills assessment, documentation support, and post-placement follow-up to ensure successful integration and long-term satisfaction.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20recruitment%20office%20with%20modern%20interior%20design%2C%20clean%20workspace%20with%20computers%20and%20documents%2C%20Middle%20Eastern%20business%20environment%2C%20professional%20atmosphere%2C%20natural%20lighting%2C%20corporate%20setting&width=600&height=400&seq=story-img&orientation=landscape"
                alt="Our Story"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-target-line text-blue-600 text-2xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('ourMission')}</h3>
              <p className="text-gray-700 leading-relaxed">
                {t('missionText')}
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-blue-600 text-2xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('ourVision')}</h3>
              <p className="text-gray-700 leading-relaxed">
                {t('visionText')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Our Achievements</h2>
            <p className="text-blue-100 text-lg">Numbers that speak for our success</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-blue-100 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our work and define our commitment to excellence
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-blue-600 text-2xl w-8 h-8 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section 
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Expertise</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced team brings deep knowledge of GCC markets and recruitment best practices
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-lg text-center">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20Middle%20Eastern%20business%20woman%20in%20modern%20office%2C%20confident%20recruitment%20specialist%2C%20clean%20corporate%20environment%2C%20professional%20headshot%20style%2C%20business%20attire&width=300&height=300&seq=team1&orientation=squarish"
                alt="Team Member"
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover object-top"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Sarah Al-Mansouri</h3>
              <p className="text-teal-600 font-medium mb-3">Senior Recruitment Manager</p>
              <p className="text-gray-600 text-sm">15+ years experience in GCC recruitment with expertise in healthcare and technical sectors.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-lg text-center">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20Middle%20Eastern%20business%20man%20in%20modern%20office%2C%20experienced%20recruitment%20director%2C%20clean%20corporate%20environment%2C%20professional%20headshot%20style%2C%20business%20suit&width=300&height=300&seq=team2&orientation=squarish"
                alt="Team Member"
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover object-top"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ahmed Hassan</h3>
              <p className="text-teal-600 font-medium mb-3">Operations Director</p>
              <p className="text-gray-600 text-sm">Specializes in construction and hospitality recruitment with extensive GCC network.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-lg text-center">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20business%20woman%20recruitment%20consultant%20in%20modern%20office%2C%20diverse%20team%20member%2C%20clean%20corporate%20environment%2C%20professional%20headshot%20style%2C%20business%20attire&width=300&height=300&seq=team3&orientation=squarish"
                alt="Team Member"
                className="w-24 h-24 rounded-full mx-auto mb-4 object-cover object-top"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Priya Sharma</h3>
              <p className="text-teal-600 font-medium mb-3">Documentation Specialist</p>
              <p className="text-gray-600 text-sm">Expert in visa processing and compliance with GCC immigration requirements.</p>
            </div>
          </div>
        </div>
      </section>
      */}
      <Footer />
    </div>
  );
};

export default About;
